/**
 * Cross-tab auth messages (Story 17.2) over `BroadcastChannel('amatis-auth')`.
 *
 * With the access token in memory and the refresh token in an HttpOnly cookie,
 * there is no shared storage for tabs to watch any more. This channel carries
 * the three facts tabs need from each other:
 *
 *   - `refreshing` — a tab has started a refresh POST. A peer that needs a
 *     refresh waits (bounded) for its result instead of racing the single-use
 *     rotation on the shared cookie.
 *   - `refreshed {accessToken, user, tenant}` — the winner's new token; every
 *     peer adopts it.
 *   - `logout` — a tab logged out; every peer clears its state and goes to
 *     /login.
 *
 * A BroadcastChannel never delivers a message to the instance that posted it,
 * so a tab does not hear itself. The channel is created lazily on first use;
 * where `BroadcastChannel` is missing (or throws) every function here is a
 * no-op and each tab simply behaves on its own.
 */

import type { Tenant, User } from '../api/auth';

export const AUTH_CHANNEL_NAME = 'amatis-auth';

export type AuthMessage =
  | { type: 'refreshing' }
  | { type: 'refreshed'; accessToken: string; user: User | null; tenant: Tenant | null }
  | { type: 'logout' };

export type AuthMessageListener = (message: AuthMessage) => void;

/** `undefined` = not created yet; `null` = unavailable in this browser. */
let channel: BroadcastChannel | null | undefined;
const listeners = new Set<AuthMessageListener>();

/** Narrow an incoming `MessageEvent.data` to an `AuthMessage`, or `null`. */
function parseMessage(data: unknown): AuthMessage | null {
  if (!data || typeof data !== 'object') return null;
  const m = data as Record<string, unknown>;
  switch (m.type) {
    case 'refreshing':
      return { type: 'refreshing' };
    case 'logout':
      return { type: 'logout' };
    case 'refreshed':
      if (typeof m.accessToken !== 'string' || m.accessToken.length === 0) return null;
      return {
        type: 'refreshed',
        accessToken: m.accessToken,
        user: m.user && typeof m.user === 'object' ? (m.user as User) : null,
        tenant: m.tenant && typeof m.tenant === 'object' ? (m.tenant as Tenant) : null,
      };
    default:
      return null;
  }
}

function getChannel(): BroadcastChannel | null {
  if (channel !== undefined) return channel;
  if (typeof BroadcastChannel === 'undefined') {
    channel = null;
    return channel;
  }
  try {
    const created = new BroadcastChannel(AUTH_CHANNEL_NAME);
    created.onmessage = (event: MessageEvent) => {
      const message = parseMessage(event.data);
      if (!message) return;
      // Snapshot: a listener may unsubscribe while we iterate.
      for (const listener of [...listeners]) {
        try {
          listener(message);
        } catch (err) {
          console.error('[authChannel] listener failed:', err);
        }
      }
    };
    channel = created;
  } catch {
    channel = null;
  }
  return channel;
}

/** Tell every OTHER tab. Never throws. */
export function postAuthMessage(message: AuthMessage): void {
  try {
    getChannel()?.postMessage(message);
  } catch (err) {
    console.error('[authChannel] post failed:', err);
  }
}

/** Hear every other tab's messages. Returns the unsubscribe. */
export function onAuthMessage(listener: AuthMessageListener): () => void {
  getChannel();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Test hook: close the channel and start a fresh one. Listeners stay
 * registered — their owners (module-level in `client.ts`, effect-scoped in
 * `AuthContext`) manage their own lifetimes — so when any remain the new
 * channel is opened at once, as a still-open tab's would be.
 */
export function __resetAuthChannelForTests(): void {
  try {
    channel?.close();
  } catch {
    // already closed
  }
  channel = undefined;
  if (listeners.size > 0) getChannel();
}
